// Báo cáo công lương: chỉ dùng số đã chốt của các kỳ đã duyệt (TỔNG LƯƠNG, ỨNG, BH NLĐ, TNCN, THỰC CHI
// trên từng dòng CT_BANG_CONG_LUONG), không tính lại. Gom theo dòng kỳ–nhân sự, theo tháng, theo tổ hoặc
// theo nhân sự.

export const REPORT_AMOUNTS = [
    { key: 'gross', field: 'TONG_LUONG', label: 'Tổng cộng' },
    { key: 'advance', field: 'TONG_UNG', label: 'Trừ ứng' },
    { key: 'insurance', field: 'BH_NLD', label: 'BH NLĐ' },
    { key: 'tax', field: 'TNCN', label: 'Thuế TNCN' },
    { key: 'net', field: 'TONG_THUC_CHI', label: 'Thực chi' },
];

export const REPORT_GROUPINGS = [
    { value: 'detail', label: 'Chi tiết' },
    { value: 'month', label: 'Theo tháng' },
    { value: 'team', label: 'Theo tổ' },
    { value: 'employee', label: 'Theo nhân sự' },
];

const NO_TEAM = 'Chưa có tổ';
const emptyAmounts = () => Object.fromEntries(REPORT_AMOUNTS.map(({ key }) => [key, 0]));
const periodLabel = (row) => `${String(row.thang).padStart(2, '0')}/${row.nam}`;

/** Mỗi dòng chi tiết của kỳ đã duyệt thành một dòng báo cáo; dòng của kỳ chưa duyệt bị bỏ. */
export function buildReportRows(periods, details) {
    const periodByCode = new Map(periods.filter((period) => period.TRANG_THAI === 'DA_DUYET').map((period) => [period.MA_KY, period]));
    return details.filter((detail) => periodByCode.has(detail.BANG_ID)).map((detail) => {
        const period = periodByCode.get(detail.BANG_ID);
        return {
            id: detail.ID,
            maKy: period.MA_KY,
            thang: period.THANG,
            nam: period.NAM,
            soHd: detail.NHAN_SU_ID,
            hoTen: detail.HO_TEN_SNAPSHOT || detail.NHAN_SU_ID,
            to: detail.TO_SNAPSHOT || NO_TEAM,
            templateId: detail.MAU_ID,
            ...Object.fromEntries(REPORT_AMOUNTS.map(({ key, field }) => [key, Number(detail[field]) || 0])),
        };
    });
}

export function filterReportRows(rows, { month = 'ALL', team = 'ALL', template = 'ALL' } = {}) {
    return rows.filter((row) => (month === 'ALL' || row.thang === Number(month))
        && (team === 'ALL' || row.to === team)
        && (template === 'ALL' || row.templateId === template));
}

/**
 * Gom dòng theo cách xem. Mỗi nhóm có key, label, sub (dòng phụ), periodCount, employeeCount và các khoản tiền.
 * Theo nhân sự thì tổ lấy tổ của kỳ gần nhất (người chuyển tổ giữa năm vẫn là một dòng).
 */
export function groupReportRows(rows, groupBy) {
    if (groupBy === 'detail') {
        return rows.map((row) => ({ ...row, key: row.id, label: row.hoTen, sub: row.soHd, periodLabel: periodLabel(row), periodCount: 1, employeeCount: 1 }));
    }
    const keyOf = { month: (row) => row.maKy, team: (row) => row.to, employee: (row) => row.soHd }[groupBy];
    if (!keyOf) throw new Error(`Không có cách gom ${groupBy}`);
    const groups = new Map();
    for (const row of rows) {
        const key = keyOf(row);
        let group = groups.get(key);
        if (!group) {
            group = { key, ...emptyAmounts(), periods: new Set(), employees: new Set(), latest: row };
            groups.set(key, group);
        }
        REPORT_AMOUNTS.forEach(({ key: amount }) => { group[amount] += row[amount]; });
        group.periods.add(row.maKy);
        group.employees.add(row.soHd);
        if (row.nam * 100 + row.thang > group.latest.nam * 100 + group.latest.thang) group.latest = row;
    }
    return Array.from(groups.values(), ({ periods, employees, latest, ...group }) => {
        const base = { ...group, periodCount: periods.size, employeeCount: employees.size, thang: latest.thang, nam: latest.nam };
        if (groupBy === 'month') return { ...base, label: `Tháng ${periodLabel(latest)}`, sub: latest.maKy, maKy: latest.maKy };
        if (groupBy === 'team') return { ...base, label: group.key, sub: `${employees.size} nhân sự` };
        return { ...base, label: latest.hoTen, sub: latest.soHd, soHd: latest.soHd, to: latest.to };
    });
}

export function summarizeReport(rows) {
    const totals = emptyAmounts();
    for (const row of rows) REPORT_AMOUNTS.forEach(({ key }) => { totals[key] += row[key]; });
    return {
        ...totals,
        periodCount: new Set(rows.map((row) => row.maKy)).size,
        employeeCount: new Set(rows.map((row) => row.soHd)).size,
    };
}
